/**
 * MyDrafts — shows the current user's draft reports for a given type.
 * data-testid: candeza-reports-drafts-*
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Button } from '@/components/common';
import { useMyDrafts, useDeleteStaffReport, type ReportType, type StaffReport } from './api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const DraftCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
`;

const DraftInfo = styled.div`
  flex: 1;
`;

const DraftTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const DraftMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Empty = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
`;

interface MyDraftsProps {
  reportType: ReportType;
  onResume: (draft: StaffReport) => void;
}

export const MyDrafts: React.FC<MyDraftsProps> = ({ reportType, onResume }) => {
  const { data, isLoading } = useMyDrafts(reportType);
  const deleteMutation = useDeleteStaffReport(reportType);
  const [discarding, setDiscarding] = useState<string | null>(null);

  const handleDiscard = async (id: string) => {
    setDiscarding(id);
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Draft discarded');
    } catch {
      toast.error('Failed to discard draft');
    } finally {
      setDiscarding(null);
    }
  };

  if (isLoading) {
    return (
      <Container>
        <div data-testid="candeza-reports-drafts-loading">Loading drafts…</div>
      </Container>
    );
  }

  const drafts = data?.items ?? [];

  if (!drafts.length) {
    return (
      <Empty data-testid="candeza-reports-drafts-empty">
        No drafts. Start a new report to save a draft.
      </Empty>
    );
  }

  return (
    <Container data-testid="candeza-reports-drafts-list">
      {drafts.map((draft) => {
        const daysLeft = Math.max(
          0,
          Math.ceil((new Date(draft.expires_at).getTime() - Date.now()) / 86_400_000),
        );
        return (
          <DraftCard
            key={draft.id}
            data-testid={`candeza-reports-drafts-row-${draft.id}`}
          >
            <DraftInfo>
              <DraftTitle>
                {draft.period_start} – {draft.period_end} ({draft.period_type})
                {draft.student_name && ` — ${draft.student_name}`}
              </DraftTitle>
              <DraftMeta>
                Updated: {new Date(draft.updated_at).toLocaleDateString()} ·{' '}
                <span style={{ color: daysLeft <= 1 ? '#ef4444' : '#92400e' }}>
                  Expires in {daysLeft}d
                </span>
              </DraftMeta>
            </DraftInfo>
            <Actions>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResume(draft)}
                data-testid={`candeza-reports-drafts-resume-${draft.id}`}
              >
                ▶ Resume
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleDiscard(draft.id)}
                disabled={discarding === draft.id}
                data-testid={`candeza-reports-drafts-discard-${draft.id}`}
              >
                {discarding === draft.id ? '…' : '🗑 Discard'}
              </Button>
            </Actions>
          </DraftCard>
        );
      })}
    </Container>
  );
};

